import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';

import { Video } from '../../types';
import { MobileFeedPlayer } from '../MobileFeedPlayer';
import Actions from './Actions/Actions';
import Footer from './Footer/Footer';
import styles from './style';

interface MobileFeedItemProps {
  index: number;
  video: Video;
  // Far from the screen: an empty block, no image is loaded.
  near: boolean;
  // Next to the screen: the slide has its video.
  withPlayer: boolean;
  current: boolean;
  playing: boolean;
  muted: boolean;
  paused: boolean;
  inTiktok: boolean;
  onTap: (index: number) => void;
  openShortModal: () => void;
  onForcedMute: () => void;
  onElement: (index: number, el: HTMLVideoElement | null) => void;
}

// One slide of the mobile feed: the video, the pause icon, the actions column and the footer.
const MobileFeedItem: React.FC<MobileFeedItemProps> = React.memo((props) => {
  const {
    index,
    video,
    near,
    withPlayer,
    current,
    playing,
    muted,
    paused,
    inTiktok,
    onTap,
    openShortModal,
    onForcedMute,
    onElement,
  } = props;
  // The icon plays its exit animation before it goes.
  const [leaving, setLeaving] = useState(false);
  const wasPausedRef = useRef(paused);

  useEffect(() => {
    if (wasPausedRef.current && !paused) {
      setLeaving(true);
    }
    wasPausedRef.current = paused;
  }, [paused]);

  const onAnimationEnd = useCallback(() => {
    setLeaving(false);
  }, []);

  // A button keeps its own click: only a tap on the video pauses it.
  const onClick = useCallback((event: React.MouseEvent) => {
    if (!(event.target as HTMLElement).closest('button')) {
      onTap(index);
    }
  }, [index, onTap]);

  if (!near) {
    return <Box sx={styles.slide} />;
  }

  return (
    <Box sx={styles.slide} onClick={onClick}>
      {withPlayer && (
        <MobileFeedPlayer
          index={index}
          video={video}
          current={current}
          playing={playing}
          muted={muted}
          onForcedMute={onForcedMute}
          onElement={onElement}
        />
      )}
      {(paused || leaving) && (
        <Box sx={paused ? styles.pauseIn : styles.pauseOut} onAnimationEnd={paused ? undefined : onAnimationEnd}>
          <FontAwesomeIcon icon={faPlay} />
        </Box>
      )}
      <Actions openShortModal={openShortModal} inTiktok={inTiktok} video={video} />
      <Footer inTiktok={inTiktok} video={video} />
    </Box>
  );
});

export default MobileFeedItem;
